"use client"
import { zodResolver } from "@hookform/resolvers/zod"
import type { FragmentOf, ResultOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import {
  BottomSheetBody,
  BottomSheetHeader,
  BottomSheetHeaderTitle,
  Flex,
  Form,
  Separator,
  Table,
} from "@opensea/ui-kit"
import { bn } from "@opensea/utils"
import { FormProvider, useForm, useWatch } from "react-hook-form"
import { useTranslations } from "use-intl"
import { Button } from "../../../../../components/Button"
import { useIsFormSubmitDisabled } from "../../../../../lib/form/valid"
import { transferFlowFormSchema } from "../../schema"
import type { TransferFlowFormValues } from "../../types"
import { TransferFlowFormItem } from "../TransferFlowFormItem"
import { TransferFlowForm_itemFragment } from "./TransferFlowForm.fragment"
import { TransferFlowRecipient } from "./TransferFlowRecipient"

type Props = {
  defaultValues?: TransferFlowFormValues | undefined
  items: FragmentOf<typeof TransferFlowForm_itemFragment>[]
  onSubmit: (values: TransferFlowFormValues) => void
}

export const TransferFlowForm = ({
  defaultValues,
  items: itemKeys,
  onSubmit,
}: Props) => {
  const t = useTranslations("wallet.TransferFlowForm")
  const items = itemKeys.map(itemKey =>
    readFragment(TransferFlowForm_itemFragment, itemKey),
  )

  const methods = useForm({
    defaultValues: defaultValues ?? getDefaultFormValues(items),
    resolver: zodResolver(transferFlowFormSchema),
    mode: "onChange",
  })

  const { handleSubmit, control } = methods
  const formItems = useWatch({ control, name: "items" })

  const tableItems = formItems.flatMap(formItem => {
    const matchedItem = items.find(item => item.id === formItem.id)

    return matchedItem ? [matchedItem] : []
  })

  const isFormSubmitDisabled = useIsFormSubmitDisabled(control)

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <BottomSheetHeader>
          <BottomSheetHeaderTitle>
            {t("title", { count: items.length })}
          </BottomSheetHeaderTitle>
        </BottomSheetHeader>

        <BottomSheetBody>
          <TransferFlowRecipient />
          <Separator className="my-0.5" />

          <Table
            itemKey={(_, index) => index}
            items={tableItems}
            renderRow={TransferFlowFormItem}
            size="lg"
          />

          <Flex className="justify-end">
            <Button disabled={isFormSubmitDisabled} type="submit">
              {t("reviewButton")}
            </Button>
          </Flex>
        </BottomSheetBody>
      </Form>
    </FormProvider>
  )
}

const getDefaultFormValues = (
  items: ResultOf<typeof TransferFlowForm_itemFragment>[],
): TransferFlowFormValues => {
  return {
    recipient: {
      identifier: "",
      resolvedAddress: "",
      isResolving: false,
    },
    items: items.map(item => ({
      ownedQuantity: bn(item.ownership?.quantity ?? "0").toNumber(),
      quantity: 1,
      id: item.id,
    })),
  }
}
